import Spinner from "../../components/Spinner";
import { ChangeEvent, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { createClinicalAlertsApi, createPatientApi } from "../../api-helpers/PatientApiHelper";
import { ToastConfig } from "../../components/Toaster";
import { IdNamePair, PatientEpisode, PatientPersonalInfo } from "patients-experience-client";
import { Datepicker, MbscCalendarEvent } from "@mobiscroll/react";
import { formatDateTask, formatDateTime, formatDateTimeShort } from "../../helper/dateFormatter";
import { UsersDto } from "users-experience-client";
import { createUsersApi } from "../../api-helpers/UserApiHelper";
import { CommandCenterConstants } from "../../constants/Lookups";
import { useDispatch, useSelector } from "react-redux";
import { setClinicalAlertReloadKey } from "../../store/slices/clinicalAlertReload";
import { RootState } from "../../store";
import AsyncTypeahead, { DataListType } from "../../components/AsyncTypeahead";

interface AddEditPatientAlertProps {
    patientAlertId?: string;
    patientId?: string;
    setOffCanvasShow: (data: boolean) => void;
    handleShowToaster: (toasterConfig: ToastConfig) => void;
    reloadGrid?: () => void;
}

interface AddEditPatientAlertForm {
    eventTypeId: string;
    episodeTypeId: string;
    episodeStatusId: string;
    startDate: Date | string;
    endDate: Date | string |  null;
    episodeAssigneeId: string;
    episodeAssignee: string;
    problem: string;
    followup: string;
    episodeCategoryId: string | undefined;
    utilizationAvoided: boolean | undefined;
}

export const AddEditAlert = ({
    patientAlertId,
    patientId,
    setOffCanvasShow,
    handleShowToaster,
    reloadGrid,
}: AddEditPatientAlertProps) => {
    const [loading, setLoading] = useState(false);
    const [validated, setValidated] = useState(false);
    const [form, setForm] = useState<AddEditPatientAlertForm>({} as AddEditPatientAlertForm);
    const [episodeTypes, setEpisodeTypes] = useState<IdNamePair[]>([]);
    const [eventTypes, setEventTypes] = useState<IdNamePair[]>([]);
    const [coachList, setCoachList] = useState<UsersDto[]>([]);
    const [episodeStatuses, setEpisodeStatuses] = useState<IdNamePair[]>([]);
    const [episodeCategories, setEpisodeCategories] = useState<IdNamePair[]>([]);
    const [patientDetails, setPatientDetails] = useState<PatientPersonalInfo>({} as PatientPersonalInfo);
    const [openPickerStartDate, setOpenPickerStartDate] = useState<boolean>(false);
    const [openPickerEndDate, setOpenPickerEndDate] = useState<boolean>(false);
    const [patientsList, setPatientsList] = useState<DataListType[]>([]);
    const [error, setError] = useState({
        startDate: false,
        endDate: false,
    });

    const dispatch = useDispatch();
    const clinicalAlertReloadKey = useSelector((state: RootState) => state.clinicalAlertReloadKey.clinicalAlertReloadKey);

    const clinicalAlertsApi = createClinicalAlertsApi();
    const coach = createUsersApi();
    const usersApi = createUsersApi();
    const patientsApi = createPatientApi();

    const loadCoachesList = async () => {
        try {
            const response = await coach.usersControllerFindAll();
            setCoachList(response.data);
        } catch (error) {
            console.log(error);
        }
    };

    const loadPatientsList = async (query: string) => {
        if (!query || query === '') {
            setPatientsList([]);
            return;
        }
        try {
            if (query.length > 2) {
                const patientsSearchRes = await patientsApi.patientsControllerSearchPatient(query);
                if (patientsSearchRes.data.length > 0) {
                    const patients = patientsSearchRes.data.map((item: any) => ({
                        key: item.key,
                        value: item.value
                    }));
                    setPatientsList(patients);
                } else {
                    setPatientsList([]);
                }
            }

        } catch (error) {
            console.log(error);
        }
    };

    const selectPatient = (selected: any) => {
        if (selected) {
            getPatientDetails(selected.key);
        }
    };

    const handleDiscard = () => {
        setOffCanvasShow(false);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setValidated(true);
        setError({
            ...error,
            startDate: !form.startDate,
        });
        if (error.startDate || error.endDate) {
            return;
        }
        if (!patientId && !patientDetails.patientId) {
            return;
        }
        // Basic validation
        if (!form.episodeTypeId || !form.episodeStatusId || !form.startDate) {
            handleShowToaster({
                type: "danger",
                title: "Please fill all required fields.",
                icon: "error_outline",
            });
            return;
        }
        try {
            setLoading(true);
            const request = {
                ...form,
                episodeAssignee: form.episodeAssigneeId,
                episodeStatusId: form.episodeStatusId,
                startDate: new Date(new Date(form.startDate).setSeconds(0, 0)),
                endDate: form.endDate ? new Date(new Date(form.endDate).setSeconds(0, 0)) : null,
            };
            if (patientAlertId) {
                await clinicalAlertsApi.clinicalAlertsControllerUpdateEpisodeById(request, patientAlertId);
                const toasterConfig = {
                    type: "success",
                    title: "Alert updated successfully!",
                    icon: "check_circle_outline",
                };
                handleShowToaster(toasterConfig as ToastConfig);
            } else {
                const patientID = patientId ? patientId : patientDetails.patientId;
                await clinicalAlertsApi.clinicalAlertsControllerSavePatientClinicalAlert(request, patientID);
                const toasterConfig = {
                    type: "success",
                    title: "Alert added successfully!",
                    icon: "check_circle_outline",
                };
                handleShowToaster(toasterConfig as ToastConfig);
                dispatch(setClinicalAlertReloadKey(clinicalAlertReloadKey + 1));
            }
            setTimeout(() => {
                handleDiscard();
            }, 1000);
            reloadGrid && reloadGrid();
        } catch (error) {
            console.error("Save error: ", error);
            handleShowToaster({
                type: "danger",
                title: patientAlertId ? "Failed to update alert!" : "Failed to add alert!",
                icon: "error_outline",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setForm((prevForm) => ({
            ...prevForm,
            [name]: checked,
        }));
    };

    const handleChange = (
        event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = event.target;
        setForm((prevForm) => ({
            ...prevForm,
            [name]: value,
        }));
    };

    const getEpisodeTypes = async () => {
        try {
            const { data } = await clinicalAlertsApi.clinicalAlertsControllerGetAllEpisodeTypes();
            setEpisodeTypes(data);
        } catch {
            console.log("Error fetching in episode types!");
        }
    }

    const getEventTypes = async () => {
        try {
            const { data } = await clinicalAlertsApi.clinicalAlertsControllerGetAllEventTypes();
            setEventTypes(data);
        } catch {
            console.log("Error fetching in event types!");
        }
    }

    const getEpisodeStatuses = async () => {
        try {
            const { data } = await clinicalAlertsApi.clinicalAlertsControllerGetAllEpisodeStatuses();
            setEpisodeStatuses(data);
        } catch {
            console.log("Error fetching in episode statuses!");
        }
    }

    const getEpisodeCategories = async () => {
        try {
            const { data } = await clinicalAlertsApi.clinicalAlertsControllerGetAllEpisodeCategories();
            setEpisodeCategories(data);
        } catch {
            console.log("Error fetching in episode categories!");
        }
    }

    const dateRangeChangeHandler = (name: "startDate" | "endDate", event: MbscCalendarEvent) => {
        const selectedDate = event.value ? new Date(event.value) : null; // Handle empty value

        setForm(prevForm => ({
            ...prevForm,
            [name]: selectedDate
        }));

        if (name === 'startDate') {
            setError({
                ...error,
                startDate: !selectedDate, // Mark error if date is cleared
            });
        } else if (name === 'endDate') {
            setError({
                ...error,
                endDate: false,
            })
        }
    };

    const getPatientAlertDetails = async () => {
        try {
            const { data } = await clinicalAlertsApi.clinicalAlertsControllerGetEpisodeById(patientAlertId!);
            setForm({
                eventTypeId: data.eventType.id,
                episodeTypeId: data.episodeType.id,
                episodeStatusId: data.episodeStatus.id,
                startDate: formatDateTime(data.startDate),
                endDate: data.endDate ? formatDateTime(data.endDate) : null,
                episodeAssigneeId: data.episodeAssignee ? data.episodeAssignee.id : '',
                episodeAssignee: data.episodeAssignee ? data.episodeAssignee.id : '',
                problem: data.problem || '',
                followup: data.followup || '',
                utilizationAvoided: data.utilizationAvoided || false,
                episodeCategoryId: data.episodeCategory ? data.episodeCategory.id : undefined,
            });
        } catch (error) {
            console.log("Error fetching in patient alert details!");
        }
    }

    const fetchCommandCenterUser = async () => {
        try {
            const commandCenterUser = await usersApi.usersControllerSearchUsers(undefined, CommandCenterConstants.COMMAND_CENTER);
            if (commandCenterUser.data.length > 0) {
                commandCenterUser.data.map((user: any) => {
                    setForm(prevForm => ({
                        ...prevForm,
                        episodeAssigneeId: user.key,
                    })
                    );
                });
            }
        } catch (error) {
            console.log("Error fetching in command center user!");
        }
    };

    const getPatientDetails = async (patientId: string) => {
        try {
            const { data } = await patientsApi.patientsControllerGetPatientPersonalInfo(patientId);
            setPatientDetails(data);
        } catch (error) {
            console.log("Error fetching in patient details!");
        }
    }

    const unsetPatientId = () => {
        setPatientsList([]); // Clear the patients list
        setPatientDetails({} as PatientPersonalInfo); // Reset patient details
    }

    useEffect(() => {
        if (error.startDate) {
            const label = document.querySelector('label[for="startDate"]');
            if (label) {
                const input = label.nextElementSibling?.querySelector('input.mbsc-textfield');
                if (input) {
                    input.classList.add('invalid-input');
                }
            }
        }
        if (error.endDate) {
            const label = document.querySelector('label[for="endDate"]');
            if (label) {
                const input = label.nextElementSibling?.querySelector('input.mbsc-textfield');
                if (input) {
                    input.classList.add('invalid-input');
                }
            }
        }
    }, [error]);

    useEffect(() => {
        if (patientId) {
            getPatientDetails(patientId);
        }
    }, [patientId]);

    useEffect(() => {
        const initializeData = async () => {
            try {
                setLoading(true);
                await Promise.all([
                    getEpisodeTypes(),
                    getEventTypes(),
                    getEpisodeStatuses(),
                    loadCoachesList(),
                    getEpisodeCategories(),
                ]);
                if (patientAlertId) {
                    await getPatientAlertDetails();
                } else {
                    await fetchCommandCenterUser();
                    // Adding default values in form
                    setForm(prevForm => ({
                        ...prevForm,
                        episodeTypeId: CommandCenterConstants.EPISODE_TYPE_NON_URGENT_ID,
                        episodeStatusId: CommandCenterConstants.EPISODE_STATUS_NEW_NEEDS_ACTION_ID,
                        startDate: new Date(), // Includes both current date and time
                    }));
                }
            } catch (error) {
                console.error("Error initializing data:", error);
            } finally {
                setLoading(false);
            }
        };
        initializeData();
    }, []);

    return (
        <>
            {loading && <Spinner />}
            {patientId && <div>
                <h6 className="m-0 fs-15">{patientDetails.firstName} {patientDetails.lastName}</h6>
                <span className="fs-11">
                    {patientDetails.wellinksId} | {formatDateTask(patientDetails.dob)}
                </span>
            </div>}
            <Form
                className="d-flex flex-column"
                noValidate
                validated={validated}
                onSubmit={handleSubmit}
            >
                {!patientId &&
                    <>
                        <div className="d-flex mb-3">
                            <div className="flex-fill position-relative">
                                <label
                                    htmlFor="Patient"
                                    className="form-label fs-12 fw-semibold mb-1"
                                >
                                    Patient
                                </label>

                                <AsyncTypeahead
                                    name="patientId"
                                    defaultValue={''}
                                    options={patientsList}
                                    onSelect={selectPatient}
                                    callback={loadPatientsList}
                                    placeholder={"Search Patient"}
                                    required={true}
                                    callbackOnEmpty={() => {
                                        setPatientsList([]); // Clear the patients list
                                        setPatientDetails({} as PatientPersonalInfo); // Reset patient details
                                    }}
                                    unsetId={unsetPatientId}
                                />
                            </div>
                            <div className="ms-2 w-35">
                                <label
                                    htmlFor="targetDate"
                                    className="form-label fs-12 fw-semibold mb-1"
                                >
                                    DOB
                                </label>
                                <div className="position-relative">
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={patientDetails ? formatDateTask(patientDetails?.dob) : ''}
                                        placeholder="MM-DD-YYYY"
                                        disabled
                                    />
                                    <span className="material-symbols-outlined fs-18 calendar-icon">
                                        calendar_month
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 mb-2">
                            <label className="form-label fs-12 fw-semibold mb-0">
                                Wellinks ID
                            </label>
                            <div className="position-relative">
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={patientDetails?.wellinksId || ''}
                                    placeholder="XXXX-XXXX"
                                    disabled
                                />
                            </div>
                        </div>
                    </>
                }
                <hr className="mt-1 mb-2" />

                <div className="flex-fill">
                    <div className="row g-2">
                        {patientAlertId ? (
                            <div className="d-flex justify-content-between row g-2 mb-2 pe-0">
                                <div className="col-6 mb-2">
                                    <label className="form-label fs-12 fw-semibold mb-0">Alert<span className="text-danger">*</span></label>
                                    <select
                                        className="form-select form-select-sm fs-12"
                                        name="eventTypeId"
                                        value={form.eventTypeId}
                                        onChange={handleChange}
                                        disabled={true}
                                        required
                                    >
                                        <option value="" >
                                            Select alert type
                                        </option>
                                        {eventTypes.length > 0 &&
                                            eventTypes.map((event: IdNamePair, index: number) => (
                                                <option key={index} value={event.id}>
                                                    {event.name}
                                                </option>
                                            ))}
                                    </select>
                                </div>
                                <div className="col-6 mb-2">
                                    <label className="form-label fs-12 fw-semibold mb-0">Alert Category</label>
                                    <select
                                        className="form-select form-select-sm fs-12"
                                        name="episodeCategoryId"
                                        value={form.episodeCategoryId}
                                        onChange={handleChange}
                                    >
                                        <option value="" >
                                            Select category
                                        </option>
                                        {episodeCategories.length > 0 &&
                                            episodeCategories.map((event: IdNamePair, index: number) => (
                                                <option key={index} value={event.id}>
                                                    {event.name}
                                                </option>
                                            ))}
                                    </select>
                                </div>
                            </div>
                        ) : null}
                        <div className="col-6 mb-2">
                            <label className="form-label fs-12 fw-semibold mb-0">
                                Type<span className="text-danger">*</span>
                            </label>
                            <select
                                className="form-select form-select-sm fs-12"
                                name="episodeTypeId"
                                value={form.episodeTypeId}
                                onChange={handleChange}
                                required
                            >
                                <option value="" >
                                    Select type
                                </option>
                                {episodeTypes.length > 0 &&
                                    episodeTypes.map((type: IdNamePair, index: number) => (
                                        <option key={index} value={type.id}>
                                            {type.name}
                                        </option>
                                    ))}
                            </select>
                        </div>
                        <div className="col-6 mb-2">
                            <label className="form-label fs-12 fw-semibold mb-0">
                                Status<span className="text-danger">*</span>
                            </label>
                            <select
                                className="form-select form-select-sm fs-12"
                                name="episodeStatusId"
                                value={form.episodeStatusId}
                                onChange={handleChange}
                                disabled={patientAlertId === undefined}
                                required
                            >
                                <option value="" >
                                    Select status
                                </option>
                                {episodeStatuses.length > 0 &&
                                    episodeStatuses.map((status: IdNamePair, index: number) => (
                                        <option key={index} value={status.id}>
                                            {status.rollup} ({status.name})
                                        </option>
                                    ))}
                            </select>
                        </div>
                        <div className="col-6 mb-3">
                            <label
                                htmlFor="startDate"
                                className="form-label fs-12 fw-semibold mb-1"
                            >
                                Start Date<span className="text-danger">*</span>
                            </label>
                            <div className="mobiscroll">
                                <div className="mobiscroll__calendar">
                                    <Datepicker
                                        id="startDate"
                                        controls={['calendar', 'time']}
                                        calendarType="month"
                                        theme="windows"
                                        themeVariant="light"
                                        placeholder="MM/DD/YYYY"
                                        display="bubble"
                                        touchUi={true}
                                        buttons={[]}
                                        cssClass={`${error.startDate ? 'invalid-input' : ''}`}
                                        value={form.startDate}
                                        showOnClick={false}
                                        isOpen={openPickerStartDate}
                                        onClose={() => setOpenPickerStartDate(false)}
                                        onChange={(e) => dateRangeChangeHandler('startDate', e)}
                                        required
                                    />
                                </div>
                                <div onClick={() => setOpenPickerStartDate(!openPickerStartDate)} className="mobiscroll__calendar-icon">
                                    <span className="material-symbols-outlined fs-18" >
                                        calendar_month
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="col-6 mb-3">
                            <label
                                htmlFor="endDate"
                                className="form-label fs-12 fw-semibold mb-1"
                            >
                                End Date
                            </label>
                            <div className="mobiscroll">
                                <div className="mobiscroll__calendar">
                                    <Datepicker
                                        id="endDate"
                                        controls={['calendar', 'time']}
                                        calendarType="month"
                                        theme="windows"
                                        themeVariant="light"
                                        placeholder="MM/DD/YYYY"
                                        display="bubble"
                                        touchUi={true}
                                        buttons={[]}
                                        cssClass={`${error.endDate ? 'invalid-input' : ''}`}
                                        value={form.endDate}
                                        showOnClick={false}
                                        isOpen={openPickerEndDate}
                                        onClose={() => setOpenPickerEndDate(false)}
                                        onChange={(e) => dateRangeChangeHandler('endDate', e)}
                                    />
                                </div>
                                <div onClick={() => setOpenPickerEndDate(!openPickerEndDate)} className="mobiscroll__calendar-icon">
                                    <span className="material-symbols-outlined fs-18" >
                                        calendar_month
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 mb-2">
                            <label className="form-label fs-12 fw-semibold mb-0">
                                Assignee
                            </label>
                            <select
                                className="form-select form-select-sm fs-12"
                                name="episodeAssigneeId"
                                value={form.episodeAssigneeId}
                                onChange={handleChange}
                            >
                                <option value="" >
                                    Select assignee
                                </option>
                                {coachList.length > 0 &&
                                    coachList.map((type: UsersDto, index: number) => (
                                        <option key={index} value={type.id}>
                                            {type.firstName} {type.lastName}
                                        </option>
                                    ))}
                            </select>
                        </div>
                        <div className="col-12">
                            <label htmlFor="Problem" className="form-label">Problem</label>
                            <textarea
                                name="problem"
                                className="form-control form-control-sm"
                                id="Problem"
                                placeholder="Add relevant notes"
                                rows={3}
                                onChange={handleChange}
                                value={form.problem || ''}
                            />
                        </div>
                        <div className="col-12">
                            <label htmlFor="followup" className="form-label">Follow Up</label>
                            <textarea
                                name="followup"
                                className="form-control form-control-sm"
                                id="followup"
                                placeholder="Add relevant notes"
                                rows={3}
                                onChange={handleChange}
                                value={form.followup || ''}
                            />
                        </div>
                        {patientAlertId ? (<div className="col-12 mb-3">
                            <div className="d-flex gap-3">
                                <label className="form-check-label fs-12 fw-semibold mb-0" htmlFor="utilizationAvoided">
                                    Utilization Avoided?
                                    <div className="text-muted fs-10">(Checked = avoided)</div>
                                </label>
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="utilizationAvoided"
                                    name="utilizationAvoided"
                                    checked={form.utilizationAvoided}
                                    onChange={handleCheckboxChange}
                                />
                            </div>
                        </div>) : null}
                    </div>
                </div>
                <div className="offcanvas-footer w-100 border-top d-flex justify-content-between border-top ">
                    <div>
                        <button
                            className="btn btn-outline-secondary rounded-pill px-5 fs-13"
                            type="reset"
                            onClick={() => setOffCanvasShow(false)}
                        >
                            Discard
                        </button>
                    </div>
                    <div className="d-flex gap-2">
                        <button
                            className="btn btn-secondary rounded-pill px-5 fs-13"
                            type="submit"
                        >
                            Submit
                        </button>
                    </div>
                </div>
            </Form>
        </>
    );
};
